import { useState } from "react";
import { HttpStatusCode, isAxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { SendToTrackingService } from "@/api/handlers/errors";
import { useApiClient } from "@/hooks/useApiClient";
import { isIpNotAllowedError } from "@/utils/ipRestriction";
import { postAiStoredPicturesChat } from "../api";
import {
  type AiStoredPicturesChatError,
  type AiStoredPicturesChatResponse,
} from "../types";

export const AI_CHAT_MAX_MESSAGE_LENGTH = 2000;
export const AI_CHAT_UNAVAILABLE_MESSAGE = "現在この機能はご利用いただけません";

export interface UseAiStoredPicturesChatResult {
  response: AiStoredPicturesChatResponse | null;
  errorMessage: string;
  isSubmitting: boolean;
  isUnavailable: boolean;
  resetSearch: () => void;
  submitMessage: (message: string) => Promise<boolean>;
}

const chatErrorMessage = (error: unknown): string => {
  if (!isAxiosError<AiStoredPicturesChatError>(error)) {
    return "検索に失敗しました。時間をおいてもう一度お試しください";
  }
  if (error.code === "ECONNABORTED") {
    return "応答を待つ時間を超えました。時間をおいてもう一度お試しください";
  }
  switch (error.response?.status) {
    case HttpStatusCode.BadRequest:
      return error.response.data.detail ?? "質問内容を確認してください";
    case HttpStatusCode.Unauthorized:
      return "ログインし直してから、もう一度お試しください";
    case HttpStatusCode.Forbidden:
    case HttpStatusCode.NotFound:
      return AI_CHAT_UNAVAILABLE_MESSAGE;
    case HttpStatusCode.TooManyRequests:
      return "時間をおいてから、もう一度お試しください";
    case HttpStatusCode.BadGateway:
    case HttpStatusCode.ServiceUnavailable:
      return "検索機能を利用できないため、時間をおいてもう一度お試しください";
    default:
      return "検索に失敗しました。時間をおいてもう一度お試しください";
  }
};

const isUnavailableChatError = (error: unknown): boolean =>
  isAxiosError(error) &&
  (error.response?.status === HttpStatusCode.Forbidden ||
    error.response?.status === HttpStatusCode.NotFound);

const reportUnexpectedChatError = (error: unknown): void => {
  if (!isAxiosError(error)) {
    Sentry.captureException(error);
    return;
  }

  const statusCode = error.response?.status;
  if (statusCode == null || statusCode >= HttpStatusCode.InternalServerError) {
    SendToTrackingService(error);
  }
};

export const useAiStoredPicturesChat = (): UseAiStoredPicturesChatResult => {
  const { apiClient } = useApiClient();
  const [response, setResponse] = useState<AiStoredPicturesChatResponse | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);

  const resetSearch = (): void => {
    setResponse(null);
    setErrorMessage("");
  };

  const submitMessage = async (message: string): Promise<boolean> => {
    const trimmedMessage = message.trim();
    if (
      trimmedMessage.length === 0 ||
      trimmedMessage.length > AI_CHAT_MAX_MESSAGE_LENGTH ||
      isSubmitting ||
      isUnavailable
    ) {
      return false;
    }

    setResponse(null);
    setErrorMessage("");
    setIsSubmitting(true);
    try {
      const result = await postAiStoredPicturesChat(apiClient, trimmedMessage);
      setResponse(result);
      return true;
    } catch (error) {
      // IP拒否の案内は共通処理に任せ、利用対象外の通知を重ねない。
      if (isIpNotAllowedError(error)) {
        return false;
      }
      reportUnexpectedChatError(error);
      const detail = chatErrorMessage(error);
      if (isUnavailableChatError(error)) {
        setIsUnavailable(true);
        setResponse(null);
        setErrorMessage(detail);
        toast.info(detail);
        return false;
      }
      setErrorMessage(detail);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    response,
    errorMessage,
    isSubmitting,
    isUnavailable,
    resetSearch,
    submitMessage,
  };
};
